import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ShoppingCart, Truck, ShieldCheck, Leaf, Sparkles } from "lucide-react";import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

const themes = {
  amber: { badge: "bg-amber-500", ring: "ring-amber-200", text: "text-amber-600", btn: "bg-amber-500 hover:bg-amber-600", glow: "from-amber-50" },
  pink: { badge: "bg-pink-500", ring: "ring-pink-200", text: "text-pink-600", btn: "bg-pink-500 hover:bg-pink-600", glow: "from-pink-50" },
  blue: { badge: "bg-sky-600", ring: "ring-sky-200", text: "text-sky-600", btn: "bg-sky-600 hover:bg-sky-700", glow: "from-sky-50" },
};

export default function Offers() {
  const { addItem } = useCart();
  const [offers, setOffers] = useState([]);
  const [pageTitle, setPageTitle] = useState("Pachete 2+1 GRATIS");
  const [pageSubtitle, setPageSubtitle] = useState(
    "La fiecare 2 detergenți gel premium cumpărați, primești 1 balsam de rufe complet gratuit — în parfumul ales de tine. Ofertă valabilă în limita stocului disponibil."
  );

  useEffect(() => {
    api.get("/offers").then((res) => setOffers(res.data));
    api.get("/settings").then((res) => {
      if (res.data?.offers_title) setPageTitle(res.data.offers_title);
      if (res.data?.offers_subtitle) setPageSubtitle(res.data.offers_subtitle);
    });
  }, []);

  const addOffer = (offer) => {
    addItem(
      { id: `offer-${offer._id}`, name: offer.title, price: offer.price, image: offer.image },
      1
    );
  };

  return (
    <div data-testid="offers-page">
      <section className="bubble-bg mx-auto max-w-7xl px-6 pt-14 lg:px-12">
        <span className="label-caps text-emerald-600">Promoție specială online</span>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tight text-slate-900 lg:text-5xl" data-testid="offers-page-title">{pageTitle}</h1>
        <p className="mt-3 max-w-2xl text-slate-600" data-testid="offers-page-subtitle">
          {pageSubtitle}
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 lg:px-12">
        <div className="grid gap-8 lg:grid-cols-3">
          {offers.map((offer) => {
            const t = themes[offer.theme] || themes.blue;
            return (
              <div
                key={offer._id}
                className={`group flex flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_8px_32px_rgba(2,132,199,0.06)] ring-1 ${t.ring} transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_56px_rgba(2,132,199,0.14)]`}
                data-testid={`offer-card-${offer.theme}`}
              >
                <Link to={`/oferta/${offer._id}`} className={`relative block overflow-hidden bg-gradient-to-b ${t.glow} to-white`}>
                  <img
                    src={offer.image}
                    alt={offer.title}
                    className="w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                </Link>
                <div className="flex flex-1 flex-col p-6">
                  <span className={`label-caps ${t.text}`}>{offer.fragrance}</span>
                  <Link to={`/oferta/${offer._id}`}>
                    <h3 className="mt-1.5 font-display text-lg font-black leading-snug text-slate-900 transition-colors hover:text-sky-700">{offer.subtitle}</h3>
                  </Link>
                  <ul className="mt-4 space-y-2">
                    {offer.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                        <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-100 text-emerald-600"><Check className="h-3 w-3" /></span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex items-end gap-2">
                    <span className="font-display text-3xl font-black text-slate-900">{formatPrice(offer.price)}</span>
                    {offer.old_price && <span className="mb-1 text-base text-slate-400 line-through">{formatPrice(offer.old_price)}</span>}
                  </div>
                  <p className="mt-1 text-sm font-semibold text-emerald-600">Economisești {formatPrice(offer.old_price - offer.price)}</p>
                  <Button
                    size="lg"
                    className={`mt-5 w-full rounded-full text-base text-white ${t.btn}`}
                    onClick={() => addOffer(offer)}
                    data-testid={`offer-add-${offer.theme}`}
                  >
                    <ShoppingCart className="mr-2 h-5 w-5" /> Adaugă pachetul
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="border-t border-slate-100 bg-slate-50/50">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:px-12">
          {[
            { icon: Leaf, t: ">90% ingrediente naturale", d: "Formule blânde cu pielea și cu hainele tale." },
            { icon: Sparkles, t: "6 enzime active", d: "Curăță în profunzime și elimină petele dificile." },
            { icon: Truck, t: "Livrare rapidă", d: "Livrăm comanda oriunde în țară prin curier." },
            { icon: ShieldCheck, t: "Plata la livrare", d: "Plătești curierului, fără riscuri online." },
          ].map((b) => (
            <div key={b.t} className="rounded-2xl bg-white p-6 shadow-[0_8px_32px_rgba(2,132,199,0.05)]">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-sky-100 text-sky-600"><b.icon className="h-5 w-5" /></div>
              <h4 className="mt-4 font-display text-base font-bold text-slate-900">{b.t}</h4>
              <p className="mt-1 text-sm text-slate-500">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-16 text-center lg:px-12">
        <h2 className="font-display text-2xl font-black text-slate-900 lg:text-3xl">Vrei produse individuale?</h2>
        <p className="mt-3 text-slate-600">Explorează întreaga gamă de detergenți și balsamuri în catalogul nostru.</p>
        <Button asChild size="lg" className="mt-6 rounded-full bg-sky-600 px-7 hover:bg-sky-500" data-testid="offers-catalog-cta">
          <Link to="/produse">Vezi catalogul</Link>
        </Button>
      </section>
    </div>
  );
}
